import {
  ApolloClient,
  ApolloProvider,
  NormalizedCacheObject,
} from '@apollo/client';
import { outputTemplateRoutes } from '@eluve-lib/frontend-feature-output-templates/output-template-routes';
import { reviewAppointmentDetailsRoutes } from '@eluve-lib/frontend-review-dash/routes';
import { ErrorBoundary } from '@sentry/react';
import { Suspense } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import 'supertokens-auth-react';
import { ActorRefFrom } from 'xstate';

import { SessionMachine } from '@eluve/apollo-client';
import {
  SingularEntityRedirect,
  TenantToOrganizationRedirect,
  lazyRoute,
} from '@eluve/app-router-utils';
import { TableSkeleton } from '@eluve/blocks';
import { LoadingIndicator, Stack, Text } from '@eluve/components';

/*
 * Every admin page is deferred. This route table is reachable from the app's
 * entry (the router needs its paths to match a URL), so a static import here
 * would put the whole admin console in the bundle that renders the login
 * screen. Route *objects* stay eager; page *code* arrives on first visit.
 */
const ExperimentPage = lazyRoute(
  'ExperimentPage',
  () => import('@eluve/experiments-admin'),
);
const ExperimentsListPage = lazyRoute(
  'ExperimentsListPage',
  () => import('@eluve/experiments-admin'),
);
const GlobalAssetDeletionHistoryPage = lazyRoute(
  'GlobalAssetDeletionHistoryPage',
  () => import('@eluve/frontend-asset-deletion-dash'),
);
const SubmitAssetDeletionRequestPage = lazyRoute(
  'SubmitAssetDeletionRequestPage',
  () => import('@eluve/frontend-asset-deletion-dash'),
);
const TenantAssetDeletionHistoryPage = lazyRoute(
  'TenantAssetDeletionHistoryPage',
  () => import('@eluve/frontend-asset-deletion-dash'),
);
const GlobalConversationStarterDefaultsPage = lazyRoute(
  'GlobalConversationStarterDefaultsPage',
  () => import('@eluve/frontend-feature-conversation-starter-defaults'),
);
const TenantConversationStarterDefaultsPage = lazyRoute(
  'TenantConversationStarterDefaultsPage',
  () => import('@eluve/frontend-feature-conversation-starter-defaults'),
);
const ArtifactDetailsPage = lazyRoute(
  'ArtifactDetailsPage',
  () => import('@eluve/frontend-feature-eval'),
);
const ArtifactListPage = lazyRoute(
  'ArtifactListPage',
  () => import('@eluve/frontend-feature-eval'),
);
const CreateArtifactPage = lazyRoute(
  'CreateArtifactPage',
  () => import('@eluve/frontend-feature-eval'),
);
const CreateDatasetPage = lazyRoute(
  'CreateDatasetPage',
  () => import('@eluve/frontend-feature-eval'),
);
const CreateWorkflowPage = lazyRoute(
  'CreateWorkflowPage',
  () => import('@eluve/frontend-feature-eval'),
);
const DatasetDetailsPage = lazyRoute(
  'DatasetDetailsPage',
  () => import('@eluve/frontend-feature-eval'),
);
const DatasetsListPage = lazyRoute(
  'DatasetsListPage',
  () => import('@eluve/frontend-feature-eval'),
);
const DuplicateWorkflowContextProvider = lazyRoute(
  'DuplicateWorkflowContextProvider',
  () => import('@eluve/frontend-feature-eval'),
);
const FactVerificationIndexPage = lazyRoute(
  'FactVerificationIndexPage',
  () => import('@eluve/frontend-feature-eval'),
);
const FactVerificationModelPromptTemplatePage = lazyRoute(
  'FactVerificationModelPromptTemplatePage',
  () => import('@eluve/frontend-feature-eval'),
);
const FactVerificationWorkflowDetailsPage = lazyRoute(
  'FactVerificationWorkflowDetailsPage',
  () => import('@eluve/frontend-feature-eval'),
);
const OutputTemplatesListPage = lazyRoute(
  'OutputTemplatesListPage',
  () => import('@eluve/frontend-feature-output-templates'),
);
const GlobalPromptDefaultsTab = lazyRoute(
  'GlobalPromptDefaultsTab',
  () => import('@eluve/frontend-feature-prompt-template-defaults'),
);
const TenantPromptDefaultsTab = lazyRoute(
  'TenantPromptDefaultsTab',
  () => import('@eluve/frontend-feature-prompt-template-defaults'),
);
const CreatePromptTemplatePage = lazyRoute(
  'CreatePromptTemplatePage',
  () => import('@eluve/frontend-feature-prompt-templates'),
);
const CreatePromptTemplateVariantPage = lazyRoute(
  'CreatePromptTemplateVariantPage',
  () => import('@eluve/frontend-feature-prompt-templates'),
);
const ModelPromptTemplateDetailsPage = lazyRoute(
  'ModelPromptTemplateDetailsPage',
  () => import('@eluve/frontend-feature-prompt-templates'),
);
const ModelsListPage = lazyRoute(
  'ModelsListPage',
  () => import('@eluve/frontend-feature-prompt-templates'),
);
const PromptTemplateDetailsLayout = lazyRoute(
  'PromptTemplateDetailsLayout',
  () => import('@eluve/frontend-feature-prompt-templates'),
);
const PromptTemplateDetailsPromptTextPage = lazyRoute(
  'PromptTemplateDetailsPromptTextPage',
  () => import('@eluve/frontend-feature-prompt-templates'),
);
const PromptTemplateModelArgsListPage = lazyRoute(
  'PromptTemplateModelArgsListPage',
  () => import('@eluve/frontend-feature-prompt-templates'),
);
const PromptTemplateUsersPage = lazyRoute(
  'PromptTemplateUsersPage',
  () => import('@eluve/frontend-feature-prompt-templates'),
);
const PromptTemplatesListPage = lazyRoute(
  'PromptTemplatesListPage',
  () => import('@eluve/frontend-feature-prompt-templates'),
);
const ReviewAppointmentLayout = lazyRoute(
  'ReviewAppointmentLayout',
  () => import('@eluve/frontend-review-dash'),
);
const SiteBannersListPage = lazyRoute(
  'SiteBannersListPage',
  () => import('@eluve/site-banners'),
);
const TranscriptLibraryListPage = lazyRoute(
  'TranscriptLibraryListPage',
  () => import('@eluve/transcript-library'),
);
const AdminAuditTrail = lazyRoute(
  'AdminAuditTrail',
  () => import('./AdminAuditTrail'),
);
const AdminLayout = lazyRoute('AdminLayout', () => import('./AdminLayout'));
const TenantGuard = lazyRoute('TenantGuard', () => import('./TenantGuard'));
const ApiTokensPage = lazyRoute(
  'ApiTokensPage',
  () => import('./api-tokens/ApiTokensPage'),
);
const BillingCodesPage = lazyRoute(
  'BillingCodesPage',
  () => import('./billing-codes/BillingCodesPage'),
);
const MlEngineersListPage = lazyRoute(
  'MlEngineersListPage',
  () => import('./components/MlEngineersListPage'),
);
const ReviewersListPage = lazyRoute(
  'ReviewersListPage',
  () => import('./components/ReviewersListPage'),
);
const GlobalFeatureFlagsPage = lazyRoute(
  'GlobalFeatureFlagsPage',
  () => import('./global-features-flags/GlobalFeatureFlagsPage'),
);
const MedplumDiagnosticsPage = lazyRoute(
  'MedplumDiagnosticsPage',
  () => import('./medplum/MedplumDiagnosticsPage'),
);
const MedplumTenantPage = lazyRoute(
  'MedplumTenantPage',
  () => import('./medplum/MedplumTenantPage'),
);
const OnCallPage = lazyRoute(
  'OnCallPage',
  () => import('./on-call/OnCallPage'),
);
const CreatePatientImportPage = lazyRoute(
  'CreatePatientImportPage',
  () => import('./patient-imports/CreatePatientImportPage'),
);
const PatientImportDetailPage = lazyRoute(
  'PatientImportDetailPage',
  () => import('./patient-imports/PatientImportDetailPage'),
);
const PatientImportFinalizePage = lazyRoute(
  'PatientImportFinalizePage',
  () => import('./patient-imports/PatientImportFinalizePage'),
);
const PatientImportsListPage = lazyRoute(
  'PatientImportsListPage',
  () => import('./patient-imports/PatientImportsListPage'),
);
const CreateTemplateBundlePage = lazyRoute(
  'CreateTemplateBundlePage',
  () => import('./template-bundles'),
);
const TemplateBundleDetailPage = lazyRoute(
  'TemplateBundleDetailPage',
  () => import('./template-bundles'),
);
const TemplateBundlesListPage = lazyRoute(
  'TemplateBundlesListPage',
  () => import('./template-bundles'),
);
const AdminTenant = lazyRoute(
  'AdminTenant',
  () => import('./tenant/$tenantId/AdminTenant'),
);
const AdminTenantBundleDetailPage = lazyRoute(
  'AdminTenantBundleDetailPage',
  () => import('./tenant/$tenantId/AdminTenantBundleDetailPage'),
);
const AdminTenantCodeTypesPage = lazyRoute(
  'AdminTenantCodeTypesPage',
  () => import('./tenant/$tenantId/AdminTenantCodeTypesPage'),
);
const AdminTenantFeatureFlagsPage = lazyRoute(
  'AdminTenantFeatureFlagsPage',
  () => import('./tenant/$tenantId/AdminTenantFeatureFlagsPage'),
);
const AdminTenantImportedOutputTemplatesPage = lazyRoute(
  'AdminTenantImportedOutputTemplatesPage',
  () => import('./tenant/$tenantId/AdminTenantImportedOutputTemplatesPage'),
);
const AdminTenantLayout = lazyRoute(
  'AdminTenantLayout',
  () => import('./tenant/$tenantId/AdminTenantLayout'),
);
const AdminTenantLlmOutputTemplatesPage = lazyRoute(
  'AdminTenantLlmOutputTemplatesPage',
  () => import('./tenant/$tenantId/AdminTenantLlmOutputTemplatesPage'),
);
const AdminTenantLlmOutputsPage = lazyRoute(
  'AdminTenantLlmOutputsPage',
  () => import('./tenant/$tenantId/AdminTenantLlmOutputsPage'),
);
const AdminTenantMlEngineersPage = lazyRoute(
  'AdminTenantMlEngineersPage',
  () => import('./tenant/$tenantId/AdminTenantMlEngineersPage'),
);
const AdminTenantPromptTemplatesPage = lazyRoute(
  'AdminTenantPromptTemplatesPage',
  () => import('./tenant/$tenantId/AdminTenantPromptTemplatesPage'),
);
const AdminTenantReviewersPage = lazyRoute(
  'AdminTenantReviewersPage',
  () => import('./tenant/$tenantId/AdminTenantReviewersPage'),
);
const AdminTenantTemplateBundlesPage = lazyRoute(
  'AdminTenantTemplateBundlesPage',
  () => import('./tenant/$tenantId/AdminTenantTemplateBundlesPage'),
);
const AdminTenantUserDefaultSettingsPage = lazyRoute(
  'AdminTenantUserDefaultSettingsPage',
  () => import('./tenant/$tenantId/AdminTenantUserDefaultSettingsPage'),
);
const AssetDeletionDash = lazyRoute(
  'AssetDeletionDash',
  () => import('./tenant/$tenantId/asset-deletion/AssetDeletionDash'),
);
const TenantDash = lazyRoute(
  'TenantDash',
  () => import('./tenant/$tenantId/dash/TenantDash'),
);
const AdminAppointment = lazyRoute(
  'AdminAppointment',
  () =>
    import(
      './tenant/$tenantId/dash/appointments/$appointmentId/AdminAppointment'
    ),
);
const AdminAppointmentOutlet = lazyRoute(
  'AdminAppointmentOutlet',
  () =>
    import(
      './tenant/$tenantId/dash/appointments/$appointmentId/AdminAppointmentOutlet'
    ),
);
const AdminAppointmentFiles = lazyRoute(
  'AdminAppointmentFiles',
  () =>
    import(
      './tenant/$tenantId/dash/appointments/$appointmentId/components/AdminAppointmentFiles'
    ),
);
const AdminAppointmentSourceArtifacts = lazyRoute(
  'AdminAppointmentSourceArtifacts',
  () =>
    import(
      './tenant/$tenantId/dash/appointments/$appointmentId/components/AdminAppointmentSourceArtifacts'
    ),
);
const AdminSummaryContent = lazyRoute(
  'AdminSummaryContent',
  () =>
    import(
      './tenant/$tenantId/dash/appointments/$appointmentId/components/AdminSummaryContent'
    ),
);
const AdminTranscriptsPage = lazyRoute(
  'AdminTranscriptsPage',
  () =>
    import(
      './tenant/$tenantId/dash/appointments/$appointmentId/components/AdminTranscriptsPage'
    ),
);
const AdminAppointmentsDash = lazyRoute(
  'AdminAppointmentsDash',
  () => import('./tenant/$tenantId/dash/appointments/AdminAppointmentsDash'),
);
const SwapAppointmentsPage = lazyRoute(
  'SwapAppointmentsPage',
  () => import('./tenant/$tenantId/swap-appointments/SwapAppointmentsPage'),
);
const AdminTenantUsers = lazyRoute(
  'AdminTenantUsers',
  () => import('./tenant/$tenantId/users/AdminTenantUsers'),
);
const AdminTenants = lazyRoute(
  'AdminTenants',
  () => import('./tenant/AdminTenants'),
);
const AdminTestEmailPage = lazyRoute(
  'AdminTestEmailPage',
  () => import('./test-email/AdminTestEmailPage'),
);
const UserPage = lazyRoute('UserPage', () => import('./users/UserPage'));
const UsersTable = lazyRoute('UsersTable', () => import('./users/UsersTable'));
export const createAdminRoutes = ({
  path,
  client,
  tenantScopedApolloClient,
  sessionActor,
  postHogProjectId,
  langfuseBaseUrl,
  langfuseProjectId,
}: {
  path: string;
  client: ApolloClient<NormalizedCacheObject>;
  tenantScopedApolloClient: ApolloClient<NormalizedCacheObject>;
  sessionActor: ActorRefFrom<SessionMachine>;
  postHogProjectId: string;
  langfuseBaseUrl?: string | null;
  langfuseProjectId?: string | null;
}) => {
  return [
    {
      path,
      // Local boundary so entering the admin console shows a spinner here
      // rather than suspending all the way up to the app-level fallback.
      element: (
        <Suspense fallback={<LoadingIndicator size="xl" />}>
          <AdminLayout />
        </Suspense>
      ),
      children: [
        {
          index: true,
          element: <Navigate to="organizations" replace={true} />,
        },
        {
          path: 'tenant/*',
          element: <TenantToOrganizationRedirect />,
        },
        {
          path: 'tenants/*',
          element: <TenantToOrganizationRedirect />,
        },
        {
          path: 'organizations',
          element: (
            <ApolloProvider client={client}>
              <AdminTenants />
            </ApolloProvider>
          ),
        },
        {
          path: 'audit-trail',
          element: (
            <ApolloProvider client={client}>
              <AdminAuditTrail />
            </ApolloProvider>
          ),
        },
        {
          path: 'fact-verification',
          element: (
            <ApolloProvider client={client}>
              <Outlet />
            </ApolloProvider>
          ),
          children: [
            {
              index: true,
              element: <FactVerificationIndexPage />,
            },
            {
              path: 'workflow/create',
              element: <CreateWorkflowPage />,
            },
            {
              path: 'workflow/copy/:workflowId',
              element: (
                <DuplicateWorkflowContextProvider>
                  <CreateWorkflowPage />
                </DuplicateWorkflowContextProvider>
              ),
            },
            {
              path: ':workflowId',
              element: <FactVerificationWorkflowDetailsPage />,
            },
            {
              path: ':workflowId/:modelPromptTemplateId',
              element: <FactVerificationModelPromptTemplatePage />,
            },
            {
              path: 'artifacts',
              element: <ArtifactListPage />,
            },
            {
              path: 'artifacts/:artifactId',
              element: <ArtifactDetailsPage />,
            },
            {
              path: 'artifacts/create',
              element: <CreateArtifactPage />,
            },
            {
              path: 'datasets',
              element: <DatasetsListPage />,
            },
            {
              path: 'datasets/:datasetId',
              element: <DatasetDetailsPage />,
            },
            {
              path: 'datasets/create',
              element: <CreateDatasetPage />,
            },
          ],
        },
        {
          path: 'models',
          element: (
            <ApolloProvider client={client}>
              <ModelsListPage />
            </ApolloProvider>
          ),
        },
        {
          path: 'template-bundles',
          element: (
            <ApolloProvider client={client}>
              <Suspense fallback={<LoadingIndicator size="xl" />}>
                <Outlet />
              </Suspense>
            </ApolloProvider>
          ),
          children: [
            {
              index: true,
              element: <TemplateBundlesListPage />,
            },
            {
              path: 'create',
              element: <CreateTemplateBundlePage />,
            },
            {
              path: ':bundleId',
              element: <TemplateBundleDetailPage />,
            },
          ],
        },
        {
          path: 'output-templates',
          element: (
            <ApolloProvider client={client}>
              <Suspense fallback={<LoadingIndicator size="xl" />}>
                <Outlet />
              </Suspense>
            </ApolloProvider>
          ),
          children: [
            {
              index: true,
              element: <OutputTemplatesListPage />,
            },
            ...outputTemplateRoutes,
          ],
        },
        {
          path: 'prompts',
          element: (
            <ApolloProvider client={client}>
              <Suspense fallback={<LoadingIndicator size="xl" />}>
                <Outlet />
              </Suspense>
            </ApolloProvider>
          ),
          children: [
            {
              index: true,
              element: <PromptTemplatesListPage />,
            },
            {
              path: 'create',
              element: (
                <CreatePromptTemplatePage onDoneNavigateTo="/admin/prompts" />
              ),
            },
            {
              path: 'clone/:promptTemplateId',
              element: (
                <CreatePromptTemplatePage onDoneNavigateTo="/admin/prompts" />
              ),
            },
            {
              path: 'clone/:promptTemplateId/variant/:variantNumber',
              element: <CreatePromptTemplateVariantPage />,
            },
            {
              path: ':promptTemplateId',
              element: <PromptTemplateDetailsLayout />,
              children: [
                {
                  index: true,
                  element: <Navigate to="prompt" replace />,
                },
                {
                  path: 'prompt',
                  element: <PromptTemplateDetailsPromptTextPage />,
                },
                {
                  path: 'prompt/variant/:variantNumber',
                  element: <PromptTemplateDetailsPromptTextPage />,
                },
                {
                  path: 'model-args',
                  element: <PromptTemplateModelArgsListPage />,
                },
                {
                  path: 'users',
                  element: <PromptTemplateUsersPage />,
                },
              ],
            },
            {
              path: ':promptTemplateId/:modelPromptTemplateId',
              element: <ModelPromptTemplateDetailsPage />,
            },
            {
              path: 'global-defaults',
              element: <GlobalPromptDefaultsTab />,
            },
          ],
        },
        {
          path: 'conversation-starters',
          element: (
            <ApolloProvider client={client}>
              <GlobalConversationStarterDefaultsPage />
            </ApolloProvider>
          ),
        },
        {
          path: 'organization/*',
          element: <SingularEntityRedirect entityName="organization" />,
        },
        {
          path: 'organizations/:tenantId',
          element: (
            <TenantGuard
              client={tenantScopedApolloClient}
              sessionActor={sessionActor}
            >
              <AdminTenantLayout />
            </TenantGuard>
          ),
          children: [
            {
              index: true,
              element: <Navigate to="manage" replace />,
            },
            {
              path: 'manage',
              element: (
                <ApolloProvider client={client}>
                  <AdminTenant />
                </ApolloProvider>
              ),
              children: [
                {
                  index: true,
                  element: <Navigate to="feature-flags" replace />,
                },
                {
                  path: 'feature-flags',
                  element: <AdminTenantFeatureFlagsPage />,
                },
                {
                  path: 'llm-outputs',
                  element: <AdminTenantLlmOutputsPage />,
                },
                {
                  path: 'prompt-templates',
                  element: <AdminTenantPromptTemplatesPage />,
                },
                {
                  path: 'llm-output-templates',
                  element: <AdminTenantLlmOutputTemplatesPage />,
                },
                {
                  path: 'imported-llm-output-templates',
                  element: <AdminTenantImportedOutputTemplatesPage />,
                },
                {
                  path: 'default-user-settings',
                  element: <AdminTenantUserDefaultSettingsPage />,
                },
                {
                  path: 'code-types',
                  element: <AdminTenantCodeTypesPage />,
                },
                {
                  path: 'reviewers',
                  element: <AdminTenantReviewersPage />,
                },
                {
                  path: 'ml-engineers',
                  element: <AdminTenantMlEngineersPage />,
                },
                {
                  path: 'prompt-defaults',
                  element: <TenantPromptDefaultsTab />,
                },
                {
                  path: 'template-bundles',
                  children: [
                    {
                      index: true,
                      element: <AdminTenantTemplateBundlesPage />,
                    },
                    {
                      path: ':bundleId',
                      element: <AdminTenantBundleDetailPage />,
                    },
                  ],
                },
                {
                  path: 'conversation-starters',
                  element: <TenantConversationStarterDefaultsPage />,
                },
              ],
            },
            {
              path: 'dash',
              element: <TenantDash />,
            },
            {
              path: 'asset-deletion',
              element: (
                <ApolloProvider client={client}>
                  <AssetDeletionDash />
                </ApolloProvider>
              ),
              children: [
                {
                  index: true,
                  element: <Navigate to="new" replace />,
                },
                {
                  path: 'new',
                  element: <SubmitAssetDeletionRequestPage />,
                },
                {
                  path: 'history',
                  element: <TenantAssetDeletionHistoryPage />,
                },
              ],
            },
            {
              path: 'appointment/*',
              element: <SingularEntityRedirect entityName="appointment" />,
            },
            {
              path: 'appointments',
              element: <AdminAppointmentsDash />,
            },
            {
              path: 'appointments/:appointmentId',
              element: (
                <AdminAppointmentOutlet
                  langfuseBaseUrl={langfuseBaseUrl}
                  langfuseProjectId={langfuseProjectId}
                />
              ),
              children: [
                {
                  index: true,
                  element: <Navigate to="details" replace />,
                },
                {
                  path: 'details',
                  element: (
                    <ErrorBoundary
                      fallback={
                        <div>
                          Failed to load appointment details. Please check your
                          permissions or user role.
                        </div>
                      }
                    >
                      <AdminAppointment />
                    </ErrorBoundary>
                  ),
                },
                {
                  path: 'artifacts',
                  element: <ReviewAppointmentLayout />,
                  // Admin dashboard overrides only the `summary` sub-tab with
                  // AdminSummaryContent (version history + generation metadata
                  // + failure reasons — admin-debugging concerns). Reviewer
                  // tools keep the shared SummaryContent via
                  // reviewAppointmentDetailsRoutes.
                  children: [
                    ...reviewAppointmentDetailsRoutes.filter(
                      (r) => r.path !== 'summary',
                    ),
                    { path: 'summary', element: <AdminSummaryContent /> },
                  ],
                },
                {
                  path: 'files',
                  element: (
                    <Suspense fallback={<TableSkeleton rowCount={10} />}>
                      <ErrorBoundary
                        fallback={
                          <Stack gap={2}>
                            <Text size="md">
                              Failed to load appointment files
                            </Text>
                            <TableSkeleton rowCount={5} />
                          </Stack>
                        }
                      >
                        <AdminAppointmentFiles />
                      </ErrorBoundary>
                    </Suspense>
                  ),
                },
                {
                  path: 'transcript-comparison',
                  element: <AdminTranscriptsPage />,
                },
                {
                  path: 'source-artifacts',
                  element: (
                    <ApolloProvider client={client}>
                      <AdminAppointmentSourceArtifacts />
                    </ApolloProvider>
                  ),
                },
              ],
            },
            {
              path: 'users',
              element: <AdminTenantUsers />,
            },
            {
              // Deliberately unlinked from any nav — reachable only by URL.
              path: 'swap-appointments',
              element: <SwapAppointmentsPage />,
            },
          ],
        },

        {
          path: 'global-feature-flags',
          element: (
            <ApolloProvider client={client}>
              <GlobalFeatureFlagsPage />
            </ApolloProvider>
          ),
        },
        {
          path: 'users',
          element: (
            <ApolloProvider client={client}>
              <Outlet />
            </ApolloProvider>
          ),
          children: [
            {
              index: true,
              element: <UsersTable />,
            },
            {
              path: ':userId',
              element: <UserPage postHogProjectId={postHogProjectId} />,
            },
          ],
        },
        {
          path: 'api-tokens',
          element: (
            <ApolloProvider client={client}>
              <ApiTokensPage />
            </ApolloProvider>
          ),
        },
        {
          path: 'patient-imports',
          element: (
            <ApolloProvider client={client}>
              <PatientImportsListPage />
            </ApolloProvider>
          ),
        },
        {
          path: 'patient-imports/new',
          element: (
            <ApolloProvider client={client}>
              <CreatePatientImportPage />
            </ApolloProvider>
          ),
        },
        {
          path: 'patient-imports/:jobId/finalize',
          element: (
            <ApolloProvider client={client}>
              <PatientImportFinalizePage />
            </ApolloProvider>
          ),
        },
        {
          path: 'patient-imports/:jobId',
          element: (
            <ApolloProvider client={client}>
              <PatientImportDetailPage />
            </ApolloProvider>
          ),
        },
        {
          path: 'reviewers-list',
          element: (
            <ApolloProvider client={client}>
              <ReviewersListPage />
            </ApolloProvider>
          ),
        },
        {
          path: 'ml-engineers-list',
          element: (
            <ApolloProvider client={client}>
              <MlEngineersListPage />
            </ApolloProvider>
          ),
        },

        {
          path: 'site-banners',
          element: (
            <ApolloProvider client={client}>
              <SiteBannersListPage />
            </ApolloProvider>
          ),
        },
        {
          path: 'transcript-library',
          element: (
            <ApolloProvider client={client}>
              <Suspense fallback={<LoadingIndicator size="xl" />}>
                <TranscriptLibraryListPage />
              </Suspense>
            </ApolloProvider>
          ),
        },
        {
          path: 'experiments',
          element: (
            <ApolloProvider client={client}>
              <Suspense fallback={<LoadingIndicator size="xl" />}>
                <Outlet />
              </Suspense>
            </ApolloProvider>
          ),
          children: [
            {
              index: true,
              element: <ExperimentsListPage />,
            },
            {
              path: ':experimentId',
              element: <ExperimentPage />,
            },
          ],
        },
        {
          path: 'asset-deletion-global-history',
          element: (
            <ApolloProvider client={client}>
              <GlobalAssetDeletionHistoryPage />
            </ApolloProvider>
          ),
        },
        {
          path: 'on-call',
          element: (
            <OnCallPage
              langfuseBaseUrl={langfuseBaseUrl}
              langfuseProjectId={langfuseProjectId}
            />
          ),
        },
        {
          path: 'billing-codes',
          element: (
            <ApolloProvider client={client}>
              <BillingCodesPage />
            </ApolloProvider>
          ),
        },
        {
          path: 'medplum',
          element: <MedplumDiagnosticsPage />,
        },
        {
          path: 'medplum/tenants/:tenantId',
          element: <MedplumTenantPage />,
        },
        {
          path: 'test-email',
          element: <AdminTestEmailPage />,
        },
      ],
    },
  ];
};
